import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Compass, Eye, HandHeart, Sparkle } from "@phosphor-icons/react";
import { useI18n } from "../i18n/I18nContext";
import { useSiteContent } from "../i18n/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CARD_ICONS = [Compass, Eye, HandHeart];

function CmsAboutImage({ imageId }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!imageId) { setSrc(null); return; }
    let c = false;
    fetch(`${API}/images/${imageId}`).then((r) => r.json()).then((j) => !c && setSrc(j.data_url)).catch(() => {});
    return () => { c = true; };
  }, [imageId]);
  if (!src) return null;
  return <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />;
}

export default function About() {
  const { t, isRTL } = useI18n();
  const { pg, pgList } = useSiteContent();
  const heading = pg("about.heading", t.about.heading);
  const body = pg("about.body", t.about.body);
  const ctaText = pg("about.cta_text", t.about.readMore);
  const cards = pgList("about.cards", t.about.cards);
  const whatHeading = pg("about.what_heading", "What we do");
  const whatBody = pg("about.what_body", "");
  const aboutImageId = pg("about.image_id", "");

  return (
    <>
      {/* Who we are */}
      <section id="about" data-testid="about-section" className="py-10 md:py-20 bg-white reveal">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1 anim anim-left">
              <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-[54px] leading-[1.05] anim anim-up"
                style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
                {heading}
              </h2>
              <p className="mt-6 text-[#1C2B3A] leading-relaxed text-lg md:text-xl font-light max-w-xl">
                {body}
              </p>
              <Button
                data-testid="about-readmore"
                asChild
                className="mt-7 text-white hover:brightness-110 font-medium"
                style={{ background: "#1B6FE0" }}
              >
                <Link to="/who-we-are">
                  {ctaText}
                  <ArrowRight size={15} weight="bold" className={isRTL ? "rtl-flip" : ""} />
                </Link>
              </Button>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 anim anim-right anim-blur">
              <div className="relative aspect-[4/3] w-full rounded-2xl overflow-hidden"
                style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)", boxShadow: "10px 10px 0 0 rgba(27,111,224,0.85)" }}
                data-testid="about-image">
                {aboutImageId ? (
                  <CmsAboutImage imageId={aboutImageId} />
                ) : (
                  <>
                    <div className="absolute inset-0 grid-bg opacity-25" />
                    <div className="absolute inset-0 flex items-center justify-center p-10">
                      <svg viewBox="0 0 200 120" className="w-full max-w-md" aria-hidden="true">
                        <g fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1">
                          <rect x="12" y="18" width="90" height="60" rx="6" />
                          <rect x="110" y="18" width="78" height="28" rx="4" />
                          <rect x="110" y="52" width="78" height="26" rx="4" />
                          <line x1="22" y1="34" x2="92" y2="34" />
                          <line x1="22" y1="46" x2="72" y2="46" />
                          <line x1="22" y1="58" x2="80" y2="58" />
                        </g>
                        <g fill="#FFFFFF">
                          <circle cx="24" cy="94" r="3.5" />
                          <circle cx="40" cy="94" r="3.5" opacity="0.7" />
                          <circle cx="56" cy="94" r="3.5" opacity="0.4" />
                        </g>
                      </svg>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-10 md:mt-12 stagger anim">
            {cards.map((c, i) => {
              const Icon = CARD_ICONS[i % CARD_ICONS.length];
              return (
                <div key={i} data-testid={`about-pillar-${i}`}
                  className="anim anim-up card-lift group p-8 rounded-2xl border border-slate-200 bg-white">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(27,111,224,0.08)", color: "#1B6FE0" }}>
                    <Icon size={18} weight="duotone" />
                  </div>
                  <h3 className="mt-5 font-heading font-semibold text-[18px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>{c.title}</h3>
                  <p className="mt-2 text-[13.5px] text-[#6B7A8D] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
